import { useState, useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import type { MapOverlay, MapOverlayUpdate } from "@/types/camera-plans";

interface MapOverlayLayerProps {
  overlays: MapOverlay[];
  selectedOverlayId: string | null;
  onSelectOverlay: (id: string | null) => void;
  onUpdateOverlay: (id: string, updates: MapOverlayUpdate) => void;
  getRelativePosition: (
    clientX: number,
    clientY: number,
  ) => { x: number; y: number } | null;
}

export function MapOverlayLayer({
  overlays,
  selectedOverlayId,
  onSelectOverlay,
  onUpdateOverlay,
  getRelativePosition,
}: MapOverlayLayerProps) {
  return (
    <>
      {overlays.map((overlay) => (
        <OverlayItem
          key={overlay.id}
          overlay={overlay}
          isSelected={selectedOverlayId === overlay.id}
          onSelect={() => onSelectOverlay(overlay.id)}
          onUpdate={(updates) => onUpdateOverlay(overlay.id, updates)}
          getRelativePosition={getRelativePosition}
        />
      ))}
    </>
  );
}

function OverlayItem({
  overlay,
  isSelected,
  onSelect,
  onUpdate,
  getRelativePosition,
}: {
  overlay: MapOverlay;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (updates: MapOverlayUpdate) => void;
  getRelativePosition: (
    clientX: number,
    clientY: number,
  ) => { x: number; y: number } | null;
}) {
  const [dragState, setDragState] = useState<{
    mode: "move" | "resize";
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    origW: number;
    origH: number;
  } | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, mode: "move" | "resize" = "move") => {
      if (overlay.locked) return;
      e.preventDefault();
      e.stopPropagation();
      onSelect();

      const pos = getRelativePosition(e.clientX, e.clientY);
      if (!pos) return;

      const state = {
        mode,
        startX: pos.x,
        startY: pos.y,
        origX: overlay.positionX,
        origY: overlay.positionY,
        origW: overlay.width,
        origH: overlay.height,
      };
      setDragState(state);

      const handleMouseMove = (me: MouseEvent) => {
        const cur = getRelativePosition(me.clientX, me.clientY);
        if (!cur) return;

        const dx = cur.x - state.startX;
        const dy = cur.y - state.startY;

        if (mode === "move") {
          onUpdate({
            positionX: Math.max(0, Math.min(1, state.origX + dx)),
            positionY: Math.max(0, Math.min(1, state.origY + dy)),
          });
        } else {
          onUpdate({
            width: Math.max(0.02, state.origW + dx),
            height: Math.max(0.02, state.origH + dy),
          });
        }
      };

      const handleMouseUp = () => {
        setDragState(null);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [overlay, onSelect, onUpdate, getRelativePosition],
  );

  if (overlay.overlayType === "line") {
    return (
      <LineOverlay
        overlay={overlay}
        isSelected={isSelected}
        onSelect={onSelect}
        onUpdate={onUpdate}
        getRelativePosition={getRelativePosition}
      />
    );
  }

  const left = `${overlay.positionX * 100}%`;
  const top = `${overlay.positionY * 100}%`;
  const width = `${overlay.width * 100}%`;
  const height = `${overlay.height * 100}%`;

  return (
    <div
      className={cn(
        "absolute z-[6]",
        isSelected && "ring-2 ring-primary ring-offset-1",
        !overlay.locked && "cursor-move",
      )}
      style={{
        left,
        top,
        width,
        height,
        transform: `translate(-50%, -50%) rotate(${overlay.rotation}deg)`,
        opacity: overlay.opacity,
      }}
      onMouseDown={(e) => handleMouseDown(e, "move")}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      <OverlayContent overlay={overlay} />

      {/* Resize handle */}
      {isSelected && !overlay.locked && (
        <div
          className="absolute bottom-0 right-0 w-3 h-3 bg-primary border border-white rounded-sm cursor-se-resize z-20 translate-x-1/2 translate-y-1/2"
          onMouseDown={(e) => handleMouseDown(e, "resize")}
        />
      )}
    </div>
  );
}

function OverlayContent({ overlay }: { overlay: MapOverlay }) {
  switch (overlay.overlayType) {
    case "image":
      return overlay.imageURL ? (
        <img
          src={overlay.imageURL}
          alt=""
          className="w-full h-full object-contain pointer-events-none"
          draggable={false}
        />
      ) : null;

    case "rectangle":
      return (
        <div
          className="w-full h-full"
          style={{
            border:
              overlay.strokeWidth > 0
                ? `${overlay.strokeWidth}px solid ${overlay.color}`
                : "none",
            backgroundColor: overlay.fill ? overlay.color : "transparent",
            borderRadius: 2,
          }}
        />
      );

    case "ellipse":
      return (
        <div
          className="w-full h-full"
          style={{
            border:
              overlay.strokeWidth > 0
                ? `${overlay.strokeWidth}px solid ${overlay.color}`
                : "none",
            backgroundColor: overlay.fill ? overlay.color : "transparent",
            borderRadius: "50%",
          }}
        />
      );

    case "text":
      return (
        <div
          className="w-full h-full flex items-center justify-center whitespace-nowrap font-semibold select-none"
          style={{
            color: overlay.color,
            fontSize: overlay.fontSize,
          }}
        >
          {overlay.label || "Label"}
        </div>
      );

    default:
      return null;
  }
}

function LineOverlay({
  overlay,
  isSelected,
  onSelect,
  onUpdate,
  getRelativePosition,
}: {
  overlay: MapOverlay;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (updates: MapOverlayUpdate) => void;
  getRelativePosition: (
    clientX: number,
    clientY: number,
  ) => { x: number; y: number } | null;
}) {
  const points = useMemo(
    () =>
      overlay.points ?? [
        { x: overlay.positionX - 0.1, y: overlay.positionY },
        { x: overlay.positionX + 0.1, y: overlay.positionY },
      ],
    [overlay.points, overlay.positionX, overlay.positionY],
  );

  const handlePointDrag = useCallback(
    (idx: number, e: React.MouseEvent) => {
      if (overlay.locked) return;
      e.preventDefault();
      e.stopPropagation();
      onSelect();

      const handleMouseMove = (me: MouseEvent) => {
        const pos = getRelativePosition(me.clientX, me.clientY);
        if (!pos) return;
        const newPoints = [...points];
        newPoints[idx] = { x: pos.x, y: pos.y };
        onUpdate({ points: newPoints });
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [overlay, points, onSelect, onUpdate, getRelativePosition],
  );

  const svgPoints = points.map((p) => `${p.x * 100}%,${p.y * 100}%`).join(" ");

  return (
    <>
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none z-[6]"
        style={{ opacity: overlay.opacity }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
      >
        <polyline
          points={svgPoints}
          fill="none"
          stroke={overlay.color}
          strokeWidth={overlay.strokeWidth}
          className="pointer-events-auto cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
        />
      </svg>

      {/* Draggable endpoint handles */}
      {isSelected &&
        points.map((p, idx) => (
          <div
            key={idx}
            className="absolute w-3 h-3 bg-primary border border-white rounded-full cursor-move z-[7] -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
            onMouseDown={(e) => handlePointDrag(idx, e)}
          />
        ))}
    </>
  );
}
